import {
  type CaptionPage,
  type CaptionRow,
  type CaptionsElement,
  captionLayoutSchema,
  captionRows,
  captionSourceAssetIds,
  captionWords,
  mapTranscriptWords,
  paginateCaptions,
  type SceneDocument,
  type SpeechRegion,
  speechRegions,
  type TimelineWord,
  type TranscriptInfo,
  type TranscriptMap,
} from "@cva/shared";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, useEffect, useMemo } from "react";
import { orpc } from "../../lib/api";
import type { AssetInfo } from "../commands";

/** Transcripts of all videos on the main track (polls while they are being created). */
export function useTranscripts(orgId: string, doc: SceneDocument) {
  const assetIds = useMemo(() => [...captionSourceAssetIds(doc.timeline)], [doc.timeline]);
  const results = useQueries({
    queries: assetIds.map((assetId) => ({
      ...orpc.media.transcript.queryOptions({ input: { orgId, assetId } }),
      refetchInterval: (query: { state: { data?: TranscriptInfo | null } }) => {
        const status = query.state.data?.status;
        return status === "queued" || status === "processing" ? 2500 : false;
      },
    })),
    combine: (list) => list.map((query) => query.data),
  });
  return useMemo(() => {
    const infos = new Map<string, TranscriptInfo | null | undefined>();
    const transcripts: Record<string, TranscriptInfo["words"]> = {};
    assetIds.forEach((assetId, index) => {
      const info = results[index];
      infos.set(assetId, info);
      if (info && info.words.length > 0) transcripts[assetId] = info.words;
    });
    return { infos, transcripts: transcripts as TranscriptMap };
  }, [assetIds, results]);
}

export interface CaptionData {
  /** Transcript status per video asset (undefined: loading, null: none). */
  infos: Map<string, TranscriptInfo | null | undefined>;
  transcripts: TranscriptMap;
  /** Every transcript word on the timeline, hidden ones included (transcript editor). */
  words: TimelineWord[];
  /** Captions as the captions element shows them. */
  pages: CaptionPage[];
  /** One entry per caption with its words, hidden ones included (caption list). */
  rows: CaptionRow[];
  /** Where someone speaks (music ducking). */
  speech: SpeechRegion[];
  captionsElement: CaptionsElement | null;
}

const EMPTY: CaptionData = {
  infos: new Map(),
  transcripts: {},
  words: [],
  pages: [],
  rows: [],
  speech: [],
  captionsElement: null,
};

export function useCaptionData(orgId: string, doc: SceneDocument): CaptionData {
  const { infos, transcripts } = useTranscripts(orgId, doc);
  const captionsElement = useMemo(
    () =>
      (Object.values(doc.elements).find((element) => element.type === "captions") as
        | CaptionsElement
        | undefined) ?? null,
    [doc.elements],
  );
  const layout = captionsElement?.layout;
  const timeline = doc.timeline;
  // Only the timeline, the transcripts and the caption layout change the result.
  return useMemo(() => {
    const view = { timeline } as SceneDocument;
    const words = mapTranscriptWords(view, transcripts);
    const pages = paginateCaptions(
      captionWords(view, transcripts),
      layout ?? captionLayoutSchema.parse({}),
    );
    return {
      infos,
      transcripts,
      words,
      pages,
      rows: captionRows(words, pages),
      speech: speechRegions(words),
      captionsElement,
    };
  }, [timeline, transcripts, layout, infos, captionsElement]);
}

export const CaptionDataContext = createContext<CaptionData>(EMPTY);

export function useCaptions(): CaptionData {
  return useContext(CaptionDataContext);
}

export interface Waveform {
  rate: number;
  peaks: number[];
}

/** Peaks of a video's or audio file's sound (for the timeline). */
export function useWaveform(url: string | null | undefined) {
  return useQuery({
    queryKey: ["waveform", url],
    queryFn: async (): Promise<Waveform> => {
      const response = await fetch(url!, { credentials: "same-origin" });
      if (!response.ok) throw new Error("Wellenform nicht verfügbar");
      return (await response.json()) as Waveform;
    },
    enabled: Boolean(url),
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 30 * 60_000,
  });
}

/**
 * Refreshes media while previews or filmstrips of used videos are still
 * being created, or their person is being cut out.
 */
export function useMediaProcessingRefresh(
  orgId: string,
  assets: Map<string, AssetInfo>,
  doc: SceneDocument,
) {
  const queryClient = useQueryClient();
  const used = new Set([
    ...(doc.timeline?.clips ?? []).map((clip) => clip.assetId),
    ...(doc.timeline?.overlays ?? []).map((clip) => clip.assetId),
    ...(doc.timeline?.audio ?? []).map((clip) => clip.assetId),
  ]);
  const processing = [...used].some((id) => {
    const asset = assets.get(id);
    return asset?.media?.status === "processing" || asset?.matte?.status === "processing";
  });
  useEffect(() => {
    if (!processing) return;
    const timer = setInterval(() => {
      void queryClient.invalidateQueries({ queryKey: orpc.media.key() });
    }, 3000);
    return () => clearInterval(timer);
  }, [processing, queryClient]);
  // orgId keeps the hook tied to one organization's cache.
  return { processing, orgId };
}
